/**
 * commissionAgreements — who is on which plan, and whether they signed for it.
 *
 * Reads `commission_assignment_agreements()` (COM-8.91). ⚠️ THIS FILE DECIDES
 * NOTHING THE DATABASE DOES NOT ALSO DECIDE. `agreement_state` is computed in
 * SQL from the linked contract's status and pinned by that migration's suite
 * (T08); what lives here is how to say it and which control it earns.
 *
 * ⚠️ `cannotIssueYet` WAS HERE AND IS GONE. It said nothing could be issued
 * because there was "no way to create" a comp agreement template, which was true
 * on 2026-08-28 when COM-8.92 wrote it and stopped being true hours later when
 * COM-9.1 and COM-9.2 shipped the writer and the authoring card. Nothing failed;
 * the screen simply started lying, which is how copy asserting a limitation
 * rots. It is replaced by `ISSUE_COPY.noTemplateYet` in `compAgreementIssue.ts`,
 * which asserts only that no template exists YET and points at where one is
 * written. Do not reintroduce a sentence here that describes a missing feature.
 *
 * ⚠️ RESEND IS REAL AND IS WIRED. `contractService.resendContract()` exists and
 * works. It simply has nothing to act on today, because reaching
 * `awaiting_signature` requires an agreement to have been issued first. That is
 * deliberate: the control is built for the state, not for today's data.
 */

/**
 * ⚠️ THE FIVE THE DATABASE RETURNS. Kept in step with the CASE in
 * `commission_assignment_agreements()`; `verify_com_8_90_91_agreements.sql` T08
 * refuses any row outside this vocabulary, so the two cannot drift silently.
 */
export const AGREEMENT_STATES = [
  'signed',
  // ⚠️ COM-9.14. The terms this person signed are STILL the terms in force; only
  // the assignment row carrying them is new. Superseding mints a new id, so the
  // signed contract points at the old one and this assignment has no agreement
  // of its own. It does not need one: same plan version, same split, so the
  // document would say the same thing.
  'previously_agreed',
  'awaiting_signature',
  'draft',
  'cancelled',
  'none',
] as const;

export type AgreementState = (typeof AGREEMENT_STATES)[number];

/**
 * ⚠️ RETURNS null RATHER THAN GUESSING, AND null IS TREATED AS OUTSTANDING.
 *
 * A sixth state added in SQL and not mirrored here must never land in the signed
 * list. The only test for signed is `=== 'signed'` (see `isOutstanding`), so an
 * unrecognised value falls to the side that gets attention rather than the side
 * that gets a tick. Failing closed here costs a wrong label; failing open would
 * report somebody as having signed a document that does not exist.
 */
export function parseAgreementState(raw: string | null | undefined): AgreementState | null {
  return (AGREEMENT_STATES as readonly string[]).includes(raw ?? '')
    ? (raw as AgreementState)
    : null;
}

/** One row of `public.commission_assignment_agreements()`, as PostgREST returns it. */
export interface AssignmentAgreementRow {
  assignment_id: string;
  tenant_id: string;
  subject_user_id: string | null;
  subject_org_id: string | null;
  subject_label: string | null;
  subject_email: string | null;
  role: string | null;
  plan_id: string | null;
  plan_name: string | null;
  plan_version_id: string | null;
  version_number: number | null;
  effective_from: string | null;
  effective_to: string | null;
  is_current: boolean | null;
  split_percent: string | number | null;
  contract_id: string | null;
  contract_status: string | null;
  contract_sent_at: string | null;
  contract_signed_at: string | null;
  agreement_state: string;
  unresolved_tokens: string[] | null;
  // COM-9.14. The signed agreement inherited from a superseded predecessor.
  // Present whether or not the terms match, because a signed document must stay
  // reachable even once it no longer describes what the person is on.
  previous_assignment_id: string | null;
  previous_contract_id: string | null;
  previous_signed_at: string | null;
  previous_terms_unchanged: boolean | null;
}

export interface AssignmentAgreement {
  assignmentId: string;
  subjectLabel: string;
  subjectEmail: string | null;
  role: string | null;
  planName: string | null;
  versionNumber: number | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  /** ⚠️ A null `is_current` is NOT current. Same rule as `ProjectedEntry.isReady`. */
  isCurrent: boolean;
  contractId: string | null;
  contractStatus: string | null;
  contractSignedAt: string | null;
  /** null when the database returned a state this build does not recognise. */
  state: AgreementState | null;
  unresolvedTokens: string[];
  /** COM-9.14. The signed agreement this assignment inherited, if any. */
  previousContractId: string | null;
  previousSignedAt: string | null;
  /** ⚠️ `false` means the inherited agreement no longer describes these terms. */
  previousTermsUnchanged: boolean;
}

export function toAgreements(rows: readonly AssignmentAgreementRow[]): AssignmentAgreement[] {
  return rows.map((r) => ({
    assignmentId: r.assignment_id,
    subjectLabel: r.subject_label ?? '(unnamed)',
    subjectEmail: r.subject_email,
    role: r.role,
    planName: r.plan_name,
    versionNumber: r.version_number,
    effectiveFrom: r.effective_from,
    effectiveTo: r.effective_to,
    isCurrent: r.is_current === true,
    contractId: r.contract_id,
    contractStatus: r.contract_status,
    contractSignedAt: r.contract_signed_at,
    state: parseAgreementState(r.agreement_state),
    unresolvedTokens: r.unresolved_tokens ?? [],
    previousContractId: r.previous_contract_id ?? null,
    previousSignedAt: r.previous_signed_at ?? null,
    // ⚠️ null is NOT "unchanged". An absent answer must never claim the terms
    // still match, because that is the direction that hides a needed signature.
    previousTermsUnchanged: r.previous_terms_unchanged === true,
  }));
}

/**
 * Anything that is not settled needs attention, including a state we cannot read.
 *
 * ⚠️ AN ALLOWLIST, NOT A NEGATION, AND THE DIFFERENCE IS THE FAIL-CLOSED
 * PROPERTY. This read `state !== 'signed'`. Adding `previously_agreed` as a
 * second settled state by writing `!== 'signed' && !== 'previously_agreed'`
 * would keep working, but a seventh state added in SQL and not mirrored here
 * would then land wherever the negation happened to put it. Listing the settled
 * states means an unrecognised value is outstanding by construction, which is
 * the side that gets attention rather than the side that gets a tick.
 */
const SETTLED: readonly AgreementState[] = ['signed', 'previously_agreed'];

export function isOutstanding(a: AssignmentAgreement): boolean {
  return a.state === null || !SETTLED.includes(a.state);
}

export interface SplitAgreements {
  signed: AssignmentAgreement[];
  outstanding: AssignmentAgreement[];
}

/**
 * ⚠️ SORTED BY NAME, NOT BY STATE. The question this card answers is "has this
 * person signed", so a reader scans for a person. Grouping by state would put
 * the same person in a different place each visit as their state moves.
 */
export function splitAgreements(rows: readonly AssignmentAgreement[]): SplitAgreements {
  const byName = (a: AssignmentAgreement, b: AssignmentAgreement) =>
    a.subjectLabel.localeCompare(b.subjectLabel);
  return {
    signed: rows.filter((r) => !isOutstanding(r)).slice().sort(byName),
    outstanding: rows.filter(isOutstanding).slice().sort(byName),
  };
}

/** What control a row earns. */
export type AgreementActionKind = 'resend' | 'open' | 'issue' | 'none';

/**
 * ⚠️ 'cancelled' AND 'none' BOTH EARN 'issue', AND THEY ARE STILL DIFFERENT ROWS.
 * The action is the same; the sentence is not. A withdrawn agreement and one
 * that never existed are different facts about a person, and the database keeps
 * them apart precisely so a screen can too. See `describeAgreementState`.
 */
export function agreementAction(state: AgreementState | null): AgreementActionKind {
  switch (state) {
    case 'signed':
      return 'none';
    // ⚠️ 'none', not 'issue'. The whole point is that no signature is being
    // asked for: offering an Issue button here would put the theatre back.
    case 'previously_agreed':
      return 'none';
    case 'awaiting_signature':
      return 'resend';
    case 'draft':
      return 'open';
    case 'cancelled':
    case 'none':
      return 'issue';
    default:
      // Unrecognised. No control, because we cannot say what it would do.
      return 'none';
  }
}

export function describeAgreementState(state: AgreementState | null): string {
  switch (state) {
    case 'signed':
      return 'Signed';
    case 'previously_agreed':
      return 'Signed under the previous assignment, terms unchanged';
    case 'awaiting_signature':
      return 'Out for signature, not signed yet';
    case 'draft':
      return 'Started, never sent';
    case 'cancelled':
      return 'Issued, then withdrawn';
    case 'none':
      return 'Never issued';
    default:
      return 'This build does not recognise the state the database returned';
  }
}

/**
 * Where the signature on a settled row actually came from.
 *
 * ⚠️ COM-9.14. A `previously_agreed` row has NO contract of its own, so
 * `contractSignedAt` is null and the signed list would print a bare "Signed" with
 * no date against somebody whose document is real and dated. Worse, it would
 * imply they signed THIS assignment, which they did not: they signed the one it
 * replaced, and the reason that still counts is that the terms did not move.
 */
export function describeSignatureProvenance(a: AssignmentAgreement): string {
  if (a.state === 'previously_agreed') {
    const when = a.previousSignedAt ? ` ${a.previousSignedAt.slice(0, 10)}` : '';
    return `Signed${when} under the assignment this one replaced, terms unchanged`;
  }
  return a.contractSignedAt ? `Signed ${a.contractSignedAt.slice(0, 10)}` : 'Signed';
}

/**
 * The note for an OUTSTANDING row that inherited a signed agreement whose terms
 * have since moved.
 *
 * ⚠️ RETURNS null WHEN THERE IS NOTHING TO SAY. This is the case the whole story
 * is about: the person signed something real, it no longer describes what they
 * are on, and the document must stay reachable rather than vanishing with the
 * superseded assignment.
 */
export function describeSupersededSignature(a: AssignmentAgreement): string | null {
  if (a.previousContractId === null || a.previousTermsUnchanged) return null;
  const when = a.previousSignedAt ? ` on ${a.previousSignedAt.slice(0, 10)}` : '';
  return `Signed a previous agreement${when}, under terms this assignment changed.`;
}

/** "Sandbox Residential (in force) v1", or a named absence. */
export function describePlan(a: AssignmentAgreement): string {
  if (!a.planName) return 'no plan version on this assignment';
  return a.versionNumber === null ? a.planName : `${a.planName} v${a.versionNumber}`;
}

/**
 * ⚠️ THE OUTSTANDING SECTION'S TITLE, EXPLANATION AND CLEAR LINE ARE NOT HERE.
 * They live in `OPS_HEALTH_COPY` (`unsignedTitle`, `unsignedExplanation`,
 * `unsignedClear`) and are asserted literally by `commissionOpsHealth.test.ts`,
 * because COM-R.167 turned that wording into a rule: the card must say "has no
 * signed agreement" and "has unresolved terms" as two separate facts. Restating
 * them here would be a second source for one sentence, which is the mirror drift
 * this epic has been bitten by repeatedly. Only genuinely NEW copy lives below.
 */
export const AGREEMENT_COPY = {
  /** The card on Setup → Agreements that holds both lists. */
  assignmentsCardTitle: 'Who has signed',
  assignmentsCardDescription:
    'Every standing assignment and whether the comp agreement behind it has been signed.',

  signedTitle: 'Signed agreements',
  signedExplanation:
    'Who has signed for their terms, and which plan version those terms are. If an amount is ever disputed, this is the document that settles it.',
  signedNone: 'Nobody has signed a comp agreement yet.',

  /** Beside a row whose assignment has already ended. */
  endedNote: 'This assignment has ended, so it is listed for the record rather than for action.',

  resendDone: 'Agreement sent again.',
  resendFailed: 'That agreement was not sent.',
} as const;
